// Tools registry - centralized tool metadata for the app
// AI report tools: credits/title/image live in libs/ai-report-tools.ts (modes[].credits).
// Individual image-gen tool configs remain in their respective files.

import {
  AI_REPORT_TOOLS,
  LIVE_AI_REPORT_TOOL_IDS,
  getAiReportDisplayCreditCost,
} from "@/libs/ai-report-tools";

export interface ToolInfo {
  id: string;
  title: string;
  description: string;
  creditCost: number;
  imageUrl: string;
  category: 'paid' | 'free';
  toolType: 'generator' | 'editor' | 'free-editor';
  isAvailable?: boolean; // For tools not yet migrated
  model?: 'google-ai' | 'replicate-ideogram' | 'replicate-ideogram-precise-edit' | 'reve-edit'; // AI model to use (defaults to google-ai)
  modelConfig?: {
    renderingSpeed?: 'Default' | 'Turbo' | 'Quality'; // For Ideogram model
  };
}

const aiReportToolsRegistry: ToolInfo[] = AI_REPORT_TOOLS.map((tool) => ({
  id: tool.id,
  title: tool.title,
  description: tool.description,
  creditCost: getAiReportDisplayCreditCost(tool),
  imageUrl: tool.imageUrl,
  category: "paid" as const,
  toolType: "editor" as const,
  isAvailable: LIVE_AI_REPORT_TOOL_IDS.has(tool.id),
}));

export const toolsRegistry: ToolInfo[] = [
  ...aiReportToolsRegistry,

  // Paid Tools
  {
    id: "ai-headshots",
    title: "AI Headshots",
    description: "Generate studio-quality AI headshots without the photoshoot from just one selfie",
    creditCost: 3,
    imageUrl: "/assets/images/tools/179.png",
    category: 'paid',
    toolType: 'generator',
    isAvailable: true,
  },
  {
    id: "ai-headshots-dev",
    title: "AI Headshots (DEV)",
    description: "DEV: Headshots with Reve Edit model - simple and fast",
    creditCost: 1,
    imageUrl: "/assets/images/tools/ai-headshots.png",
    category: 'paid',
    toolType: 'generator',
    isAvailable: true,
    model: 'reve-edit',
  },
  {
    id: "ai-avatars",
    title: "AI Profile Pictures",
    description: "Try new looks, visit dream destinations, and create impossible photos  from one selfie",
    creditCost: 2,
    imageUrl: "/assets/images/tools/ai_photoshoot_promo_4.png",
    category: 'paid',
    toolType: 'generator',
    isAvailable: true,
  },
  {
    id: "ai-avatars-dev",
    title: "AI Photoshoot (DEV)",
    description: "DEV: Photoshoot with Reve Edit model - simple and fast",
    creditCost: 1,
    imageUrl: "/assets/images/tools/ai_photoshoot_promo_7.png",
    category: 'paid',
    toolType: 'generator',
    isAvailable: true,
    model: 'reve-edit',
  },
  {
    id: "basic-headshots",
    title: "Basic Headshots",
    description: "Create a polished headshot by enhancing your existing photo, change background and unblur",
    creditCost: 1,
    imageUrl: "/assets/images/tools/basic-headshots.png",
    category: 'paid',
    toolType: 'generator',
    isAvailable: true,
  },
  {
    id: "ai-photo-editor",
    title: "AI Photo Editor",
    description: "One-tap retouch, try-on, and camera looks from a single selfie",
    creditCost: 2,
    imageUrl: "/assets/images/tools/ai-designer.png",
    category: "paid",
    toolType: "editor",
    isAvailable: true,
  },
  {
    id: "ai-designer",
    title: "Edit with Chat",
    description: "Say what to change and the rest of your photo stays exactly the same - fixes weird AI glitches too",
    creditCost: 2,
    imageUrl: "/assets/images/tools/ai-designer.png",
    category: 'paid',
    toolType: 'editor',
    isAvailable: true,
    model: 'replicate-ideogram-precise-edit',
  },
  {
    id: "ai-designer-dev",
    title: "Edit with Chat (DEV)",
    description: "DEV: Edit with Chat - Nano Banana models",
    creditCost: 1,
    imageUrl: "/assets/images/tools/ai-designer.png",
    category: 'paid',
    toolType: 'editor',
    isAvailable: true,
  },
  {
    id: "hd-detailer",
    title: "HD Upscale & Detailer",
    description: "Add fine details to your photos with AI-powered generative upscaling.",
    creditCost: 1,
    imageUrl: "/assets/images/tools/hd-detailer.png",
    category: 'paid',
    toolType: 'editor',
    isAvailable: true,
  },
  {
    id: "change-clothes",
    title: "Change Clothes",
    description: "Try on different outfits and styles without changing your actual clothes.",
    creditCost: 1,
    imageUrl: "/assets/images/tools/change-clothes.png",
    category: 'paid',
    toolType: 'editor',
    isAvailable: true,
  },
  {
    id: "change-hair",
    title: "Change Hair & Color",
    description: "Experiment with different hairstyles and hair colors without visiting a salon.",
    creditCost: 1,
    imageUrl: "/assets/images/tools/change-hair-color.png",
    category: 'paid',
    toolType: 'editor',
    isAvailable: true,
  },
  {
    id: "ai-makeup",
    title: "AI Makeup Try On",
    description: "Try 55+ makeup styles from Latina glam to bridal elegance. Upload your photo and see yourself in any look.",
    creditCost: 1,
    imageUrl: "/assets/images/tools/ai-makeup.png",
    category: 'paid',
    toolType: 'editor',
    isAvailable: true,
  },
  {
    id: "change-scene",
    title: "Change Scene",
    description: "Place yourself in different locations and environments for unique profile pictures.",
    creditCost: 1,
    imageUrl: "/assets/images/tools/change-scene.png",
    category: 'paid',
    toolType: 'editor',
    isAvailable: true,
  },
  {
    id: "add-accessories",
    title: "Add Accessories",
    description: "Enhance your look with glasses, jewelry, hats, and other accessories.",
    creditCost: 1,
    imageUrl: "/assets/images/tools/add-accessories.png",
    category: 'paid',
    toolType: 'editor',
    isAvailable: true,
  },
  {
    id: "remove-accessories",
    title: "Remove Accessories",
    description: "Remove glasses, jewelry, hats, or other accessories from your photo.",
    creditCost: 1,
    imageUrl: "/assets/images/tools/remove-accessories.png",
    category: 'paid',
    toolType: 'editor',
    isAvailable: true,
  },
  {
    id: "clear-skin",
    title: "Clear Skin",
    description: "Enhance your complexion by removing blemishes, smoothing skin, and improving overall appearance.",
    creditCost: 1,
    imageUrl: "/assets/images/tools/clear-skin.png",
    category: 'paid',
    toolType: 'editor',
    isAvailable: true,
  },
  {
    id: "add-smile",
    title: "Add Smile",
    description: "Enhance your expression with a natural-looking smile for more approachable profile pictures.",
    creditCost: 1,
    imageUrl: "/assets/images/tools/add-smile.png",
    category: 'paid',
    toolType: 'editor',
    isAvailable: true,
  },
  {
    id: "ai-branding",
    title: "AI Branding",
    description: "Create professional branded team photos by changing clothing and adding company logos with consistent placement.",
    creditCost: 2,
    imageUrl: "/assets/images/tools/ai-branding.png",
    category: 'paid',
    toolType: 'editor',
    isAvailable: true,
  },
  {
    id: "add-logo-watermark",
    title: "Add Watermark & Logo",
    description: "Add professional watermarks and company logos to your images for branding and content protection.",
    creditCost: 1,
    imageUrl: "/assets/images/tools/add-logo-watermark.png",
    category: 'paid',
    toolType: 'editor',
    isAvailable: true,
  },
  {
    id: "ai-background",
    title: "AI Background",
    description: "Replace your background with AI-generated scenes that complement your profile picture.",
    creditCost: 1,
    imageUrl: "/assets/images/tools/ai-background.png",
    category: 'paid',
    toolType: 'editor',
    isAvailable: true,
  },
  {
    id: "age-filter",
    title: "Age Filter",
    description: "See how you might look younger or older with our advanced age transformation tool.",
    creditCost: 1,
    imageUrl: "/assets/images/tools/age-filter.png",
    category: 'paid',
    toolType: 'editor',
    isAvailable: true,
  },
  {
    id: "gender-swap",
    title: "Gender Swap",
    description: "See how you might look as a different gender with our AI gender transformation tool.",
    creditCost: 1,
    imageUrl: "/assets/images/tools/gender-swap.png",
    category: 'paid',
    toolType: 'editor',
    isAvailable: true,
  },
  {
    id: "cartoonizer",
    title: "Cartoonizer",
    description: "Transform your photo into a cartoon-style character for fun and unique profile pictures.",
    creditCost: 1,
    imageUrl: "/assets/images/tools/cartoonizer.png",
    category: 'paid',
    toolType: 'editor',
    isAvailable: true,
  },
  {
    id: "anime-styling",
    title: "Anime Styling",
    description: "Transform your photo into anime-style art for unique and creative profile pictures.",
    creditCost: 1,
    imageUrl: "/assets/images/tools/anime-styling.png",
    category: 'paid',
    toolType: 'editor',
    isAvailable: true,
  },
  {
    id: "change-light-dev",
    title: "Change Lightning (DEV)",
    description: "Apply professional photography lighting to enhance your photos with studio and natural lighting setups.",
    creditCost: 1,
    imageUrl: "/assets/images/tools/change-scene.png",
    category: 'paid',
    toolType: 'editor',
    isAvailable: true,
  },

  // Free Tools
  {
    id: "pfpmaker-legacy",
    title: "PFP Maker Legacy",
    description: "Generate 100 Profile Pictures with different backgrounds and styles in a grid instantly",
    creditCost: 0,
    imageUrl: "/assets/images/tools/legacy-pfpmaker.png",
    category: 'free',
    toolType: 'free-editor',
    isAvailable: true,
  },
  {
    id: "remove-background",
    title: "Remove Background",
    description: "Quickly remove the background from your profile picture to create a clean, professional look.",
    creditCost: 0,
    imageUrl: "/assets/images/tools/free-remove-bg.png",
    category: 'free',
    toolType: 'free-editor',
    isAvailable: true,
  },
  {
    id: "professional-backgrounds",
    title: "Professional Backgrounds",
    description: "Add polished, professional backgrounds to your profile picture perfect for LinkedIn, corporate sites, and business profiles.",
    creditCost: 0,
    imageUrl: "/assets/images/tools/free-professional.png",
    category: 'free',
    toolType: 'free-editor',
    isAvailable: true,
  },
  {
    id: "creative-backgrounds",
    title: "Creative Backgrounds",
    description: "Express your personality with artistic, creative backgrounds for social media profiles and personal branding.",
    creditCost: 0,
    imageUrl: "/assets/images/tools/free-creative.png",
    category: 'free',
    toolType: 'free-editor',
    isAvailable: true,
  },
  {
    id: "color-gradient-backgrounds",
    title: "Color & Gradient Backgrounds",
    description: "Apply stunning solid colors or smooth gradients as backgrounds to make your profile picture pop.",
    creditCost: 0,
    imageUrl: "/assets/images/tools/free-color-bg.png",
    category: 'free',
    toolType: 'free-editor',
    isAvailable: true,
  },
  {
    id: "borders",
    title: "Borders",
    description: "Add stylish borders to frame your profile picture and make it stand out on any platform.",
    creditCost: 0,
    imageUrl: "/assets/images/tools/free-border.png",
    category: 'free',
    toolType: 'free-editor',
    isAvailable: true,
  },
  {
    id: "text",
    title: "Text",
    description: "Add custom text, captions, or your name to your profile picture with various fonts and styles.",
    creditCost: 0,
    imageUrl: "/assets/images/tools/free-text.png",
    category: 'free',
    toolType: 'free-editor',
    isAvailable: true,
  },
  {
    id: "effects",
    title: "Effects",
    description: "Enhance your profile picture with filters, overlays, and visual effects to create a unique look.",
    creditCost: 0,
    imageUrl: "/assets/images/tools/free-effects.png",
    category: 'free',
    toolType: 'free-editor',
    isAvailable: true,
  },
  {
    id: "decals",
    title: "Decals",
    description: "Add stickers, badges, and decorative elements to personalize your profile picture.",
    creditCost: 0,
    imageUrl: "/assets/images/tools/free-decals.png",
    category: 'free',
    toolType: 'free-editor',
    isAvailable: true,
  },
  {
    id: "photo-filters",
    title: "Photo Filters",
    description: "Transform your photos with Instagram-style filters. Apply vintage, cinematic, and artistic effects instantly.",
    creditCost: 0, // FREE with full usage tracking
    imageUrl: "/assets/images/tools/photo-filters.png",
    category: 'free',
    toolType: 'editor',
    isAvailable: true,
  },


];

// Helper functions
export const getPaidTools = () => toolsRegistry.filter(tool => tool.category === 'paid');
export const getFreeTools = () => toolsRegistry.filter(tool => tool.category === 'free');
export const getAvailableTools = () => toolsRegistry.filter(tool => tool.isAvailable !== false);
export const getGeneratorTools = () => toolsRegistry.filter(tool => tool.toolType === 'generator');
export const getEditorTools = () => toolsRegistry.filter(tool => tool.toolType === 'editor');
export const getFreeEditorTools = () => toolsRegistry.filter(tool => tool.toolType === 'free-editor');
